import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { asCount, asNumber } from '@/lib/numbers'
import { copy } from './copy'
import { formatDateOfBirth, formatInstantDateTime, formatMoment, formatReportDate, visitByDate } from './datetime'
import type { EscalationReport, ReportRow } from './types'

const text = copy.report
const missing = text.tables.missing

/** `?` for a metric the read model does not carry, rather than a fabricated zero. */
function formatMetric(value: number | string | null | undefined): string {
  const parsed = asNumber(value)
  return parsed === null ? '?' : String(parsed)
}

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <tr>
      <td className="border border-gray-300 px-3 py-2 font-medium text-gray-800">{label}</td>
      <td className="border border-gray-300 px-3 py-2 text-right font-bold text-gray-900">{value}</td>
    </tr>
  )
}

const ESCALATION_LEVELS = {
  Emergent: { badge: 'bg-red-200 text-red-800', days: null, label: text.recommendation.emergent, fallback: text.recommendation.emergentText },
  Urgent: { badge: 'bg-orange-200 text-orange-800', days: 2, label: text.recommendation.urgent, fallback: text.recommendation.urgentFallback },
  Immediate: { badge: 'bg-yellow-200 text-yellow-800', days: 7, label: text.recommendation.immediate, fallback: text.recommendation.immediateFallback },
  Routine: { badge: 'bg-blue-100 text-blue-800', days: 30, label: text.recommendation.routine, fallback: text.recommendation.routineFallback },
} as const

type EscalationLevel = keyof typeof ESCALATION_LEVELS

function levelOf(row: ReportRow): EscalationLevel | null {
  const value = row.escalationUrgency
  return value !== null && value !== undefined && value in ESCALATION_LEVELS ? (value as EscalationLevel) : null
}

const isPatientRequest = (row: ReportRow) => row.urgency === 'PatientRequest'

function RecommendationCell({ row, timeZone }: { row: ReportRow; timeZone: string }) {
  if (isPatientRequest(row)) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-block bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap text-gray-800">
          {row.resolutionLabel ?? missing}
        </span>
        <span>{row.escalationReason ?? missing}</span>
      </div>
    )
  }

  const level = levelOf(row)
  if (level === null) return <span>{missing}</span>

  const { badge, days, label, fallback } = ESCALATION_LEVELS[level]
  const due = days !== null && row.resolvedAt ? visitByDate(row.resolvedAt, days, timeZone) : null

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className={cn('inline-block px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap', badge)}>{label}</span>
      <span>{due === null ? fallback : text.recommendation.visitBy(due)}</span>
    </div>
  )
}

type ReportColumn = {
  key: keyof typeof text.tables
  width: string
  cell: (row: ReportRow, index: number, timeZone: string) => ReactNode
  className?: string
}

const LABELLED_CELL =
  'before:mb-1 before:block before:font-semibold before:text-gray-600 before:content-[attr(data-label)] md:before:content-none'

const ESCALATION_COLUMNS: readonly ReportColumn[] = [
  { key: 'number', width: '2.5rem', className: 'hidden md:table-cell', cell: (_row, index) => index + 1 },
  {
    key: 'patient',
    width: 'auto',
    className: 'bg-blue-50 font-bold text-gray-900 md:bg-transparent md:font-medium',
    cell: (row) => row.patient.name,
  },
  {
    key: 'dateOfBirth',
    width: 'auto',
    className: LABELLED_CELL,
    cell: (row) => (row.patient.dateOfBirth ? formatDateOfBirth(row.patient.dateOfBirth) : missing),
  },
  { key: 'provider', width: 'auto', className: LABELLED_CELL, cell: (row) => row.providerName ?? missing },
  {
    key: 'recommendation',
    width: '16rem',
    className: LABELLED_CELL,
    cell: (row, _index, timeZone) => <RecommendationCell row={row} timeZone={timeZone} />,
  },
]

function ReportSection({
  title,
  isEmpty,
  className,
  children,
}: {
  title: string
  isEmpty: boolean
  className?: string
  children: ReactNode
}) {
  return (
    <div className={cn('mt-6', className)}>
      <h2 className="border-b border-gray-300 pb-2 text-base font-bold text-gray-800">{title}</h2>
      {isEmpty ? <p className="mt-3 text-sm italic text-gray-500">{text.tables.empty}</p> : children}
    </div>
  )
}

function ReportTable({ title, rows, timeZone }: { title: string; rows: ReportRow[]; timeZone: string }) {
  return (
    <ReportSection title={title} isEmpty={rows.length === 0}>
      <div className="mt-3">
        <table className="block w-full border-collapse text-left text-sm md:table md:table-fixed">
          <colgroup className="hidden md:table-column-group">
            {ESCALATION_COLUMNS.map((column) => (
              <col key={column.key} style={{ width: column.width }} />
            ))}
          </colgroup>
          <thead className="hidden md:table-header-group">
            <tr>
              {ESCALATION_COLUMNS.map((column) => (
                <th key={column.key} className="border border-gray-300 bg-blue-50 px-3 py-2 text-xs font-semibold text-gray-800">
                  {text.tables[column.key]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="block md:table-row-group">
            {rows.map((row, index) => (
              <tr key={row.ticketId} className="mt-3 block border border-gray-300 first:mt-0 md:table-row md:border-0">
                {ESCALATION_COLUMNS.map((column) => (
                  <td
                    key={column.key}
                    data-label={text.tables[column.key]}
                    className={cn(
                      'block border-b border-gray-300 px-3 py-2 text-gray-800 last:border-b-0 md:table-cell md:border md:last:border-b',
                      column.className,
                    )}
                  >
                    {column.cell(row, index, timeZone)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ReportSection>
  )
}

type CardField = { label: string; value: string; span?: 1 | 2 | 3 | 4 }

const CARD_SPANS = { 1: 'md:col-span-1', 2: 'md:col-span-2', 3: 'md:col-span-3', 4: 'md:col-span-4' } as const

function CardFields({ fields }: { fields: readonly CardField[] }) {
  return (
    <dl className="md:grid md:grid-cols-4">
      {fields.map((field) => (
        <div
          key={field.label}
          className={cn(
            'grid grid-cols-1 content-start border-b border-gray-300 md:border-r md:last:border-r-0',
            CARD_SPANS[field.span ?? 1],
          )}
        >
          <dt className="px-3 pt-2 font-semibold text-gray-600">{field.label}</dt>
          <dd className="px-3 pt-1 pb-2 break-words text-gray-800">{field.value}</dd>
        </div>
      ))}
    </dl>
  )
}

function CardSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <h3 className="border-b border-gray-300 bg-blue-50 px-3 py-2 text-base font-bold text-gray-800">{title}</h3>
      {children}
    </>
  )
}

function PatientCard({ row, timeZone, className }: { row: ReportRow; timeZone: string; className?: string }) {
  const { patient } = row
  const level = levelOf(row)

  return (
    <div className={cn('mt-6 border border-gray-300 text-left text-sm', className)}>
      <CardSection title={text.patientInformation.title}>
        <CardFields
          fields={[
            { label: text.patientInformation.name, value: patient.name },
            {
              label: text.patientInformation.dateOfBirth,
              value: patient.dateOfBirth ? formatDateOfBirth(patient.dateOfBirth) : missing,
            },
            { label: text.patientInformation.phoneNumber, value: patient.phoneNumber ?? missing },
            { label: text.patientInformation.provider, value: row.providerName ?? missing },
          ]}
        />
      </CardSection>
      <CardSection title={text.systemEscalation.title}>
        <CardFields
          fields={[
            { label: text.systemEscalation.date, value: row.createdAt ? formatInstantDateTime(row.createdAt, timeZone) : missing },
            { label: text.systemEscalation.reason, value: row.escalationReason ?? missing, span: 3 },
          ]}
        />
      </CardSection>
      <CardSection title={text.careManagerEscalation.title}>
        <CardFields
          fields={[
            {
              label: text.careManagerEscalation.date,
              value: row.resolvedAt ? formatInstantDateTime(row.resolvedAt, timeZone) : missing,
            },
            { label: text.careManagerEscalation.careManagerName, value: row.careManagerName ?? missing },
            {
              label: text.careManagerEscalation.urgency,
              value: isPatientRequest(row)
                ? (row.resolutionLabel ?? missing)
                : level === null
                  ? missing
                  : text.recommendation.category(ESCALATION_LEVELS[level].label, ESCALATION_LEVELS[level].fallback),
              span: 2,
            },
          ]}
        />
        <CardFields fields={[{ label: text.careManagerEscalation.request, value: row.escalationComment ?? missing, span: 4 }]} />
      </CardSection>
    </div>
  )
}

function PatientInteractions({ report, timeZone }: { report: EscalationReport; timeZone: string }) {
  const escalated = [...report.urgent, ...report.routine, ...(report.patientRequests ?? [])]
  const groups = new Map<string, ReportRow[]>()
  for (const row of report.notEscalated) {
    const label = row.resolutionLabel || missing
    groups.set(label, [...(groups.get(label) ?? []), row])
  }

  return (
    <ReportSection
      title={text.tables.patientInteractionsTitle}
      isEmpty={escalated.length === 0 && groups.size === 0}
      className={cn(escalated.length > 0 && 'print:break-before-page')}
    >
      {escalated.map((row, index) => (
        <PatientCard key={row.ticketId} row={row} timeZone={timeZone} className={cn(index > 0 && 'print:break-before-page')} />
      ))}
      {[...groups].map(([label, group]) => (
        <div key={label} className="mt-4">
          <h3 className="text-sm font-semibold text-gray-800">{label}</h3>
          <p className="mt-1 text-sm text-gray-800">
            {group.map((row) => row.patient.name).join(', ')}
          </p>
        </div>
      ))}
    </ReportSection>
  )
}

export function EscalationReportView({ report }: { report: EscalationReport }) {
  const timeZone = report.timezone || 'UTC'
  const { stats } = report
  const overview = [
    {
      label: text.dailyOverview.interactions,
      value: stats.outbound ? String(asCount(stats.outbound.voice) + asCount(stats.outbound.sms)) : formatMetric(null),
    },
    { label: text.dailyOverview.patientsEngaged, value: formatMetric(stats.patientsEngaged) },
    { label: text.dailyOverview.evaluationsTriggered, value: formatMetric(stats.evaluationsTriggered) },
    { label: text.dailyOverview.evaluationsCompleted, value: formatMetric(stats.evaluationsCompleted) },
    { label: text.dailyOverview.patientsEscalated(report.workspaceName), value: formatMetric(stats.patientsEscalated) },
    { label: text.dailyOverview.patientsUrgentlyEscalated, value: formatMetric(stats.patientsUrgentlyEscalated) },
  ]

  return (
    <section className="rounded-lg border border-gray-300 bg-white p-6 shadow-sm sm:p-8">
      <div className="text-center">
        <h1 className="text-lg font-bold text-gray-900">{text.title}</h1>
        <p className="mt-1 text-sm text-gray-700">{formatReportDate(report.date)}</p>
        {report.periodStart && report.periodEnd && (
          <p className="text-sm text-gray-700">
            {text.period(formatMoment(report.periodStart, timeZone), formatMoment(report.periodEnd, timeZone))}
          </p>
        )}
        <p className="text-sm text-gray-700">{report.workspaceName}</p>
      </div>

      <div className="mt-6">
        <h2 className="border-b border-gray-300 pb-2 text-base font-bold text-gray-800">{text.dailyOverview.title}</h2>
        <div className="mt-3">
          <table className="w-full table-fixed border-collapse text-left text-sm">
            <colgroup>
              <col />
              <col className="w-20 md:w-64" />
            </colgroup>
            <thead>
              <tr>
                <th className="border border-gray-300 bg-blue-50 px-3 py-2 text-xs font-semibold text-gray-800">
                  {text.dailyOverview.metric}
                </th>
                <th className="border border-gray-300 bg-blue-50 px-3 py-2 text-right text-xs font-semibold text-gray-800">
                  {text.dailyOverview.count}
                </th>
              </tr>
            </thead>
            <tbody>
              {overview.map((row) => (
                <MetricRow key={row.label} label={row.label} value={row.value} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ReportTable title={text.tables.urgentTitle} rows={report.urgent} timeZone={timeZone} />
      <ReportTable title={text.tables.routineTitle} rows={report.routine} timeZone={timeZone} />
      <ReportTable title={text.tables.patientRequestsTitle} rows={report.patientRequests ?? []} timeZone={timeZone} />
      <PatientInteractions report={report} timeZone={timeZone} />
    </section>
  )
}
